import React, { useState } from 'react';
import { CustomTour } from '../../types';

interface CustomTourListProps {
  customTours: CustomTour[];
  onUpdateCustomTour: (customTour: CustomTour) => void;
  onDeleteCustomTour: (customTourId: string) => void;
  loading?: boolean;
}

const CustomTourList: React.FC<CustomTourListProps> = ({ 
  customTours, 
  onUpdateCustomTour, 
  onDeleteCustomTour, 
  loading = false 
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState<{ customTourId: string; country: string; months: string[] } | null>(null);


  const handleDeleteClick = (customTour: CustomTour) => {
    setDeleteConfirm({ 
      customTourId: customTour.id, 
      country: customTour.country, 
      months: customTour.months || []
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDeleteCustomTour(deleteConfirm.customTourId);
      setDeleteConfirm(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary-deep-blue)]"></div>
        <span className="ml-2 text-[var(--secondary-slate)]">Loading custom tours...</span>
      </div>
    );
  }

  if (customTours.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-[var(--secondary-slate)]">No custom tours found. Create your first custom tour entry above!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {customTours.map((customTour) => (
        <div key={customTour.id} className="card">
          <div className="card-content">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Custom Tour Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="heading-tertiary text-[var(--primary-deep-blue)]">
                    {customTour.country}
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex flex-col">
                    <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Country</span>
                    <span className="text-[var(--secondary-slate)]">{customTour.country}</span>
                  </div>
                  
                  <div className="flex flex-col">
                    <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Months</span>
                    <div className="flex flex-wrap gap-1">
                      {(customTour.months || []).map((month, index) => (
                        <span 
                          key={index}
                          className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                        >
                          {month}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
              </div>
              
              {/* Action Buttons */}
              <div className="flex-shrink-0 flex flex-col gap-2">
                <button
                  onClick={() => onUpdateCustomTour(customTour)}
                  className="btn btn-primary"
                >
                  Update Entry
                </button>
                <button
                  onClick={() => handleDeleteClick(customTour)}
                  className="btn btn-danger"
                >
                  Delete Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="heading-tertiary text-[var(--primary-deep-blue)] mb-4">
              Confirm Delete
            </h3>
            <p className="body-regular text-[var(--secondary-slate)] mb-6">
              Are you sure you want to delete the custom tour entry for "{deleteConfirm.country}" with months: {deleteConfirm.months.join(', ')}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="btn btn-danger"
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTourList;
